import { buildPaletteAlert, type ComponentAlertPalette } from "./alertPalette";
import colors from "./base.colors.light";

const componentAlert: ComponentAlertPalette = {
  info: {
    filledBg: colors.blue[700],
    filledColor: colors.blue[100],
    standardBg: colors.blue[800],
    standardColor: colors.blue[100],
  },
  success: {
    filledBg: colors.green[900],
    filledColor: colors.green[200],
    standardBg: colors.green[900],
    standardColor: colors.green[200],
  },
  warning: {
    filledBg: colors.orange[900],
    filledColor: colors.orange[400],
    standardBg: colors.orange[950],
    standardColor: colors.orange[400],
  },
  error: {
    filledBg: colors.red[900],
    filledColor: colors.red[500],
    standardBg: colors.red[900],
    standardColor: colors.red[500],
  },
};

const lightPalette = {
  // palette values for light mode
  primary: {
    main: colors.defy[400],
    light: colors.defy[700],
    lightest: colors.defy[800],
    dark: colors.defy[300],
    darkest: colors.defy[100],
  },
  secondary: {
    main: colors.defy[500],
    light: colors.defy[700],
  },
  neutral: {
    dark: colors.grey[600],
    main: colors.grey[500],
    light: colors.grey[900],
    lightest: colors.grey[950],
    contrastText: colors.green[900],
  },
  background: {
    default: colors.blue[900],
    light: colors.defy[900],
    paper: colors.common.white,
  },
  action: {
    active: colors.grey[300],
    hover: colors.grey[950],
    selected: colors.blue[800],
  },
  text: {
    primary: colors.grey[200],
    secondary: colors.grey[600],
    light: colors.grey[700],
    dark: colors.grey[100],
    disabled: colors.grey[700],
  },
  success: {
    lightest: colors.green[900], // see tailwind-base.css for more details
    light: colors.green[800],
    main: colors.green[500],
    dark: colors.green[200],
    //contrastText: lightColors.green[900],
    contrastText: colors.green[700],
  },
  warning: {
    lightest: colors.orange[950], // see tailwind-base.css for more details
    light: colors.orange[800],
    main: colors.orange[600],
    dark: colors.orange[400],
    //contrastText: lightColors.green[900],
    contrastText: colors.green[700],
  },
  error: {
    lightest: colors.red[900], // see tailwind-base.css for more details
    light: colors.red[800],
    main: colors.red[500],
    dark: colors.red[500],
    contrastText: colors.red[500],
  },
  info: {
    lightest: colors.blue[800], // see tailwind-base.css for more details
    light: colors.blue[700],
    main: colors.blue[600],
    dark: colors.blue[100],
    contrastText: colors.blue[500],
  },
  grey: {
    fade: colors.grey[800],
    bold: colors.grey[700],
    light: colors.primary[400],
    dark: colors.grey[100],
    100: colors.grey[100],
    200: colors.grey[200],
    300: colors.grey[300],
    400: colors.grey[400],
    500: colors.grey[500],
    600: colors.grey[600],
    700: colors.grey[700],
    800: colors.grey[800],
    900: colors.grey[900],
    950: colors.grey[950],
    999: colors.grey[999],
  },
  Alert: buildPaletteAlert(componentAlert),
  components: {
    MuiDataGrid: {
      rowHover: colors.grey[950],
    },
  },
  Chip: {
    defaultBorder: colors.grey[800],
  },
  table: {
    rowHover: colors.grey[950],
    selectedRow: colors.defy[900],
  },
  TableCell: {
    border: colors.grey[900],
  },
  DataGrid: {
    // Container background
    bg: colors.common.white,
    // Pinned rows and columns background
    // pinnedBg: '#f1f5f9',
    // Column header background
    headerBg: colors.common.white,
  },
  LinearProgress: {
    successBg: colors.green[900],
    warningBg: colors.orange[900],
    errorBg: colors.red[900],
    infoBg: colors.blue[900],
    neutralBg: colors.grey[900],
  },
  runStatus: {
    unknown: colors.grey[900],
    disabled: colors.grey[600],
    running: colors.blue[600],
    success: colors.green[500],
    completed: colors.green[500],
    completed_with_retries: colors.green[500],
    with_retries: colors.green[500],
    preempted: colors.red[600],
    failed: colors.red[600],
    error: colors.red[600],
    completed_with_errors: colors.green[500],
    failed_with_errors: colors.red[600],
  },
  topbar: {
    background: colors.common.white,
  },
  sidebar: {
    text: colors.grey[100],
  },
  chartColorsByType: {
    cost: colors.defy[500],
    waste: colors.pink[900],
  },
  stackedBarChart: ["#cdb4db", "#ffc8dd", "#ffafcc", "#bde0fe", "#a2d2ff"],
  charts: {
    testLayer: colors.defy[700],
    palette: [
      colors.blue[600],
      colors.deepPurple[600],
      colors.orange[600],
      colors.cyan[600],
      colors.pink[600],
      colors.amber[600],
      colors.lime[600],
      colors.blueGrey[600],
      colors.teal[600],
      colors.lightGreen[600],
      colors.indigo[600],
      colors.yellow[400],
    ],
    sparkMetrics: {
      background: [
        colors.grey[700],
        colors.grey[500],
        colors.grey[800],
        colors.grey[600],
        colors.grey[700],
        colors.grey[400],
      ],
      overlay: [
        colors.defy[400],
        colors.defy[300],
        colors.defy[500],
        colors.defy[200],
        colors.defy[700],
      ],
      secondary: [
        colors.orange[600],
        colors.orange[400],
        colors.orange[800],
        colors.orange[600],
        colors.orange[800],
        colors.orange[400],
      ],
    },
    /**
     * Timeline chart chrome. Keyed to the same grey steps in both palettes, so
     * each token flips with the mode instead of staying light: the `neutral`
     * scale is literal (its `lightest` is near-white in dark mode too), which
     * is why these don't reuse it.
     */
    timeline: {
      /**
       * Bars carrying no status of their own — a finished run's executors.
       * Shaped like a palette colour so it can stand in for `error`/`warning`/
       * `info` at the call site: `light` fills, `main` edges.
       */
      defaultTone: {
        main: colors.grey[500],
        light: colors.grey[900],
      },
      /** Fill for bars that read as absence — idle executor cores. */
      idleBar: colors.grey[950],
      /** Edge of those bars: one step up from the fill. */
      idleBorder: colors.grey[900],
      /** Rule between timeline lines, and between stacked blocks. */
      separator: colors.grey[800],
      /** Plate behind a non-transparent timeline. */
      gantt: colors.grey[950],
    },
  },
} as const;

export default lightPalette;
